import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'

export function PayOrderPage() {
  const { orderId } = useParams<{ orderId: string }>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [redirecting, setRedirecting] = useState(false)

  useEffect(() => {
    if (orderId) {
      generatePaymentLink()
    }
  }, [orderId])

  async function generatePaymentLink() {
    try {
      setLoading(true)
      setError(null)

      // Call API to generate fresh payment link
      const response = await fetch(`/api/payment/generate-link/${orderId}`, {
        method: 'POST',
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to generate payment link')
      }

      const data = await response.json()

      // Redirect to SumUp payment page
      setRedirecting(true)
      window.location.href = data.paymentLink

    } catch (err: any) {
      console.error('Failed to generate payment link:', err)
      setError(err.message || 'Failed to generate payment link')
      setLoading(false)
    }
  }

  if (loading || redirecting) {
    return (
      <main className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-center text-secondary-700">
              {redirecting ? 'Redirecting to Payment...' : 'Preparing Payment...'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center gap-4 py-8">
              <div aria-hidden="true" className="animate-spin motion-reduce:animate-none rounded-full h-12 w-12 border-b-2 border-secondary-600"></div>
              <p role="status" className="text-gray-600 text-center">
                {redirecting
                  ? 'Taking you to the secure payment page...'
                  : 'Generating your payment link...'}
              </p>
            </div>
          </CardContent>
        </Card>
      </main>
    )
  }

  if (error) {
    return (
      <main className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-center text-red-700">Payment Not Available</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800 text-center">
                  This payment link is not currently available.
                </p>
              </div>

              <div className="space-y-3">
                <p className="text-sm text-gray-600 text-center">
                  This could be because:
                </p>
                <ul className="text-xs text-gray-600 space-y-1 list-disc list-inside">
                  <li>The order has already been paid</li>
                  <li>The payment link is not valid</li>
                  <li>The order has been cancelled</li>
                </ul>
                <p className="text-sm text-gray-600 text-center pt-2">
                  If you believe this is an error, please contact us for assistance.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    )
  }

  return null
}
